import React from "react";
import { apiGet, apiDelete, useDataUnlessForbidden } from "../services/api";
import IUser from "../models/IUser";
import { useSnackbar } from "notistack";
import UsersTable from "../components/projectusers/UsersTable";
import IContainer from "./IContainer";
import IProjectHeader from "../models/IProjectHeader";
import { makeStyles, createStyles } from "@material-ui/styles";
import { Theme } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import AddCustomer from "../components/AddCustomer";
import BulkAddCustomers from "../components/BulkAddCustomer";
import ModalDialog, { useModalDialog } from "../components/ModalDialog";
import UserProfileForm from "../components/projectusers/UserProfileForm";
import UserPreferencesForm from "../components/projectusers/UserPreferencesForm";

interface IManageProjectUsersContainer extends IContainer {
    projectId: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            minHeight: "400px"
        },
        icon: {
            marginRight: theme.spacing(2),
            padding: theme.spacing(4),
            borderRadius: "8px",
            "&:hover": {
                backgroundColor: theme.palette.secondary.main
            }
        },
        header: {
            minHeight: "59px"
        },
        suggestions: {
            verticalAlign: "middle",
            display: "inline-flex"
        },
        buttons: {
            padding: theme.spacing(4, 8, 4)
        }
    })
);

const ManageProjectUsersContainer: React.FC<IManageProjectUsersContainer> = ({
    setSubtitle,
    projectId,
    setIsForbidden
}) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [project, isProjectLoading] = useDataUnlessForbidden<IProjectHeader>(
        "projects/" + projectId,
        () =>
            enqueueSnackbar(
                `We couldn't retrieve the project information. Please log out and try again.`,
                { variant: "error" }
            )
    );

    const [
        projectUsers,
        isProjectUsersLoading,
        isProjectForbidden,
        refreshProjectUsers
    ] = useDataUnlessForbidden<IUser[]>(
        "projects/all/" + projectId + "/users",
        () =>
            enqueueSnackbar(
                `We couldn't find which users have access to this project. Please log out and try again.`,
                { variant: "error" }
            )
    );

    const [, , isUserListForbidden] = useDataUnlessForbidden<IUser[]>(
        "users/all",
        () =>
            enqueueSnackbar(
                `We couldn't get the list of users. Please log out and try again.`,
                { variant: "error" }
            )
    );

    const [
        isProcessingAddOrRemove,
        setIsProcessingAddOrRemove
    ] = React.useState(false);

    React.useEffect(
        () => setIsForbidden(isProjectForbidden || isUserListForbidden),
        [isProjectForbidden, isUserListForbidden, setIsForbidden]
    );

    React.useEffect(() => {
        if (isProjectLoading === false && project !== null) {
            setSubtitle(
                `These are the users who have access to the project '${
                    project!.name
                }'`
            );
        }
    }, [project, isProjectLoading, setSubtitle]);

    const removeUserHandler = async (userId: string) => {
        try {
            setIsProcessingAddOrRemove(true);

            const user = (await apiGet("users/all/" + userId)) as IUser;
            const sbkey = enqueueSnackbar(`Removing user ${user.email}`, {
                variant: "info"
            });
            await apiDelete("projects/all/" + projectId + "/users/" + user.id);
            if (sbkey) {
                closeSnackbar(sbkey);
            }
            enqueueSnackbar(`Removed ${user.email}`, { variant: "success" });
            refreshProjectUsers();
        } catch {
            enqueueSnackbar(`An error occurred while removing this user`, {
                variant: "error"
            });
        } finally {
            setIsProcessingAddOrRemove(false);
        }
    };

    const classes = useStyles();

    const [profileModalOpen, , toggleProfileModal] = useModalDialog();
    const [preferencedModalOpen, , togglePreferencedModal] = useModalDialog();

    const [user, setUser] = React.useState<IUser | undefined>(undefined);

    const handleUpdateProfile = (user: IUser) => {
        setUser(user);

        toggleProfileModal();
    };

    const handleUpdatPreferences = (user: IUser) => {
        setUser(user);

        togglePreferencedModal();
    };

    return (
        <div className={classes.root}>
            <AddCustomer
                projectId={Number.parseInt(projectId)}
                projectName={project?.name ?? ""}
                onSuccess={() => {
                    refreshProjectUsers();
                }}
            />
            <BulkAddCustomers
                projectId={Number.parseInt(projectId)}
                projectName={project?.name ?? ""}
                onSuccess={() => {
                    refreshProjectUsers();
                }}
            />
            {isProjectLoading ? (
                <div style={{ marginTop: "16px" }}>
                    {[...Array(4)].map(() => (
                        <Skeleton
                            variant="rect"
                            width={"1232px"}
                            height={"56px"}
                            style={{ marginBottom: "8px" }}
                        />
                    ))}
                </div>
            ) : (
                <>
                    <UsersTable
                        users={projectUsers}
                        isLoading={isProjectUsersLoading}
                        onRemoveUser={removeUserHandler}
                        onUpdateProfile={handleUpdateProfile}
                        onUpdatePreferences={handleUpdatPreferences}
                        isProcessing={isProcessingAddOrRemove}
                    />
                    <ModalDialog
                        isActive={preferencedModalOpen}
                        title={`Update Preferences for ${user?.email}`}
                        confirmDisabled={false}
                        confirmAction={() => {
                            togglePreferencedModal();
                        }}
                        confirmButtonText={"confirmButtonText"}
                        handleClose={() => {
                            togglePreferencedModal();
                        }}
                        preventClose={false}
                        showError={false}
                        disableDialogActions={true}
                        errorText={"errorText"}
                    >
                        <UserPreferencesForm
                            userId={user?.id}
                            onPreferencesUpdated={() => {
                                togglePreferencedModal();
                            }}
                        />
                    </ModalDialog>
                    <ModalDialog
                        isActive={profileModalOpen}
                        title={`Update Profile for ${user?.email}`}
                        confirmDisabled={false}
                        confirmAction={() => {
                            toggleProfileModal();
                        }}
                        confirmButtonText={"confirmButtonText"}
                        handleClose={() => {
                            toggleProfileModal();
                        }}
                        preventClose={false}
                        showError={false}
                        disableDialogActions={true}
                        errorText={"errorText"}
                    >
                        <UserProfileForm
                            userId={user?.id}
                            onProfileUpdated={() => {
                                toggleProfileModal();
                            }}
                        />
                    </ModalDialog>
                </>
            )}
        </div>
    );
};

export default ManageProjectUsersContainer;
